import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface QuestionDef {
  id: string;
  type: 'multiline' | 'choice';
  labelKey: string;
}

const questionDefs: QuestionDef[] = [
  { id: 'q1', type: 'multiline', labelKey: 'qualification.questions.q1.label' },
  { id: 'q2', type: 'choice', labelKey: 'qualification.questions.q2.label' },
  { id: 'q3', type: 'choice', labelKey: 'qualification.questions.q3.label' },
  { id: 'q4', type: 'choice', labelKey: 'qualification.questions.q4.label' },
  { id: 'q5', type: 'choice', labelKey: 'qualification.questions.q5.label' },
  { id: 'q6', type: 'choice', labelKey: 'qualification.questions.q6.label' },
  { id: 'q7', type: 'choice', labelKey: 'qualification.questions.q7.label' },
  { id: 'q8', type: 'choice', labelKey: 'qualification.questions.q8.label' },
  { id: 'q9', type: 'choice', labelKey: 'qualification.questions.q9.label' },
  { id: 'q10', type: 'multiline', labelKey: 'qualification.questions.q10.label' },
  { id: 'q11', type: 'choice', labelKey: 'qualification.questions.q11.label' },
  { id: 'q12', type: 'multiline', labelKey: 'qualification.questions.q12.label' },
  { id: 'q13', type: 'multiline', labelKey: 'qualification.questions.q13.label' },
  { id: 'q14', type: 'choice', labelKey: 'qualification.questions.q14.label' },
  { id: 'q15', type: 'multiline', labelKey: 'qualification.questions.q15.label' },
  { id: 'q16', type: 'choice', labelKey: 'qualification.questions.q16.label' },
];

export default function QualificationSlides() {
  const { t } = useTranslation();
  const [printReady, setPrintReady] = useState(false);
  const [autoPrinted, setAutoPrinted] = useState(false);

  // Auto-trigger print dialog on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      window.print();
      setAutoPrinted(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handlePrint = () => {
    setPrintReady(true);
    setTimeout(() => {
      window.print();
    }, 200);
  };

  return (
    <>
      {/* Screen-only controls */}
      <div className="print-hide fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm border-b border-foreground-200/50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            旋轉門篩選PPT_v1
          </span>
          <span className="text-xs text-foreground-400">16 slides</span>
          {autoPrinted && (
            <span className="text-xs text-accent-500">
              <i className="ri-check-line mr-1" />
              列印對話框已自動開啟
            </span>
          )}
        </div>
        <button
          onClick={handlePrint}
          className="whitespace-nowrap px-5 py-2 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer"
        >
          <i className="ri-printer-line mr-1" />
          {autoPrinted ? '再次列印 / 匯出 PDF' : '列印 / 匯出 PDF'}
        </button>
      </div>

      {/* Slide Deck */}
      <div className="pt-14">

        {/* Cover Slide */}
        <section className="slide bg-background-50 min-h-screen flex items-center justify-center px-8">
          <div className="text-center max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-foreground-400 mb-8">
              The Revolving Gate Portal
            </div>
            <h1
              className="text-4xl md:text-5xl text-foreground-900 mb-4 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              旋轉門篩選
            </h1>
            <p className="text-lg text-foreground-500 mb-2">
              Journey Readiness Qualification
            </p>
            <p className="text-sm text-foreground-400 mb-10">
              旅程準備度 — 16 題真誠對話
            </p>
            <div className="w-16 h-px bg-foreground-200 mx-auto mb-6" />
            <p className="text-xs text-foreground-400">
              旋轉門篩選PPT_v1.pdf
            </p>
            <p className="text-xs text-foreground-300 mt-1">
              離線使用 · 列印友善
            </p>
          </div>
        </section>

        {/* Question Slides */}
        {questionDefs.map((q, idx) => {
          const slideNum = idx + 1;
          const label = t(`${q.labelKey}`);
          const labelEn = t(`${q.labelKey}En`);
          const isChoice = q.type === 'choice';

          return (
            <section key={q.id} className="slide bg-white min-h-screen flex items-center px-10 py-16">
              <div className="w-full max-w-3xl mx-auto">
                {/* Slide Number */}
                <div className="flex items-center gap-4 mb-12">
                  <span className="text-3xl font-bold text-accent-500" style={{ fontFamily: 'var(--font-heading)' }}>
                    Q{slideNum}
                  </span>
                  <span className="text-xs tracking-wider uppercase text-foreground-300">
                    / 16
                  </span>
                  <div className="flex-1 h-px bg-foreground-100 ml-2" />
                  {isChoice && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-400">
                      選擇題
                    </span>
                  )}
                  {!isChoice && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-400">
                      書寫題
                    </span>
                  )}
                </div>

                {/* Question */}
                <h2
                  className="text-2xl md:text-3xl text-foreground-900 mb-4 leading-relaxed"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {label}
                </h2>
                <p className="text-sm text-foreground-400 mb-10 italic">
                  {labelEn}
                </p>

                {/* Answer Area */}
                {isChoice ? (
                  <div className="space-y-3">
                    {(() => {
                      const opts: string[] = i18n.getResource(
                        i18n.language,
                        'translation',
                        `${q.labelKey.replace('.label', '')}.options`
                      ) as string[] || [];
                      return Array.isArray(opts) ? opts : [];
                    })()
                      .map((option: string) => (
                        <div
                          key={option}
                          className="flex items-center gap-3 px-5 py-3.5 border border-foreground-200 rounded-md"
                        >
                          <div className="w-4 h-4 rounded-full border-2 border-foreground-300 shrink-0" />
                          <span className="text-base text-foreground-700">{option}</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="border border-foreground-200 rounded-md p-5 min-h-[180px]">
                    <p className="text-sm text-foreground-300 italic">
                      （書寫回應區 — 300 字以內）
                    </p>
                    <div className="mt-4 space-y-1">
                      <div className="h-px bg-foreground-100" />
                      <div className="h-px bg-foreground-100" />
                      <div className="h-px bg-foreground-100" />
                      <div className="h-px bg-foreground-100" />
                      <div className="h-px bg-foreground-100" />
                      <div className="h-px bg-foreground-100" />
                      <div className="h-px bg-foreground-100" />
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-16 pt-6 border-t border-foreground-100 flex items-center justify-between text-xs text-foreground-300">
                  <span>旋轉門篩選PPT_v1</span>
                  <span>{slideNum} / 16</span>
                </div>
              </div>
            </section>
          );
        })}

        {/* End Slide */}
        <section className="slide bg-background-50 min-h-screen flex items-center justify-center px-8">
          <div className="text-center max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-foreground-400 mb-8">
              The Revolving Gate Portal
            </div>
            <h2
              className="text-3xl md:text-4xl text-foreground-900 mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              旅程準備度對話結束
            </h2>
            <p className="text-base text-foreground-500 mb-2">
              Journey Readiness Conversation Complete
            </p>
            <div className="w-16 h-px bg-foreground-200 mx-auto mb-6" />
            <p className="text-sm text-foreground-400 max-w-md mx-auto leading-relaxed">
              AI Angel 從不評判你的答案。<br />
              它只是輕輕地問，靜靜地聽。
            </p>
            <p className="text-xs text-foreground-300 mt-1 italic">
              AI Angel never judges. It only asks gently, and listens quietly.
            </p>
          </div>
        </section>

      </div>
    </>
  );
}